import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Leaf } from "lucide-react";

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136",
  500: "#689689", 400: "#83E8BA",
  c100: "#f0ebe6", c50: "#faf8f5",
  m300: "#c4b8ae", m600: "#9a8a7e",
};

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold mb-3" style={{ color: F.c100, fontFamily: "Chivo, sans-serif" }}>{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed" style={{ color: F.m300 }}>
      {children}
    </div>
  </section>
);

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: F[950] }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b px-6 py-4 flex items-center gap-4" style={{ background: F[950], borderColor: F[800] }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} style={{ color: F.m300 }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: F[500] }}>
            <Leaf className="w-4 h-4" style={{ color: F.c50 }} />
          </div>
          <span className="font-black text-sm" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>EV Hub Studio</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>Privacy Policy</h1>
        <p className="text-sm mb-10" style={{ color: F.m600 }}>Last updated: 25 April 2026</p>

        <Section title="1. Who We Are">
          <p>EV Hub Studio is a product of <strong style={{ color: F.c100 }}>Stuthi Energy Private Limited</strong>, a company incorporated in India. We build AI-powered EV charging infrastructure planning tools for developers, fleet operators, and property owners.</p>
          <p>For privacy-related queries, contact us at <a href="mailto:privacy@evhubstudio.com" style={{ color: F[400] }}>privacy@evhubstudio.com</a>.</p>
        </Section>

        <Section title="2. What Data We Collect">
          <p><strong style={{ color: F.c100 }}>Account data:</strong> Name and email address when you register.</p>
          <p><strong style={{ color: F.c100 }}>Project data:</strong> Site details, coordinates, charger specifications, and financial inputs you enter to generate plans and ROI projections.</p>
          <p><strong style={{ color: F.c100 }}>Usage data:</strong> Pages visited, features used, session duration — collected in aggregate for product improvement.</p>
          <p><strong style={{ color: F.c100 }}>Contact form submissions:</strong> Name, email, and message content when you write to us.</p>
        </Section>

        <Section title="3. How We Use Your Data">
          <p>We use your data to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Deliver and improve the EV Hub Studio service</li>
            <li>Generate AI-powered site plans using the Anthropic Claude API — your project inputs are sent to Anthropic's API to produce analysis</li>
            <li>Send transactional emails (account verification, plan exports)</li>
            <li>Respond to contact form submissions</li>
            <li>Detect and prevent fraud or abuse</li>
          </ul>
          <p>We do <strong style={{ color: F.c100 }}>not</strong> sell your personal data to third parties.</p>
        </Section>

        <Section title="4. Third-Party Services">
          <p>We share data with the following processors to operate the service:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong style={{ color: F.c100 }}>Anthropic</strong> — AI inference for site analysis and recommendations (your project data is sent to their API)</li>
            <li><strong style={{ color: F.c100 }}>MongoDB Atlas</strong> — database hosting for project and account data</li>
            <li><strong style={{ color: F.c100 }}>Cloudflare</strong> — CDN and edge delivery for the web application</li>
            <li><strong style={{ color: F.c100 }}>Render</strong> — backend API hosting</li>
          </ul>
          <p>Each processor is contractually obligated to protect your data and use it only as instructed.</p>
        </Section>

        <Section title="5. Data Retention">
          <p>Account and project data is retained while your account is active. You may request deletion at any time by emailing <a href="mailto:privacy@evhubstudio.com" style={{ color: F[400] }}>privacy@evhubstudio.com</a>. We will delete or anonymise your data within 30 days of a valid request.</p>
        </Section>

        <Section title="6. Security">
          <p>All data is transmitted over HTTPS. Passwords are hashed and never stored in plain text. API credentials are stored as environment variables, never in source code. We conduct periodic security reviews.</p>
        </Section>

        <Section title="7. Your Rights (DPDP Act 2023)">
          <p>Under India's Digital Personal Data Protection Act 2023, you have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate data</li>
            <li>Request erasure of your data</li>
            <li>Withdraw consent at any time</li>
            <li>Nominate a representative to exercise these rights on your behalf</li>
          </ul>
          <p>To exercise any right, email <a href="mailto:privacy@evhubstudio.com" style={{ color: F[400] }}>privacy@evhubstudio.com</a>. We will respond within 72 hours.</p>
        </Section>

        <Section title="8. Cookies">
          <p>We use only essential cookies for session authentication. We do not use advertising or tracking cookies. No cookie consent banner is required.</p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>We may update this policy from time to time. Material changes will be notified via email or an in-app banner. Continued use of the service after notification constitutes acceptance.</p>
        </Section>

        <Section title="10. Contact">
          <p>Stuthi Energy Private Limited<br />
          Email: <a href="mailto:privacy@evhubstudio.com" style={{ color: F[400] }}>privacy@evhubstudio.com</a></p>
        </Section>
      </main>

      <footer className="text-center py-8 text-xs border-t" style={{ color: F.m600, borderColor: F[800] }}>
        © {new Date().getFullYear()} Stuthi Energy Private Limited
      </footer>
    </div>
  );
}
